import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  MagnifyingGlassIcon,
  ChartBarIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  BoltIcon,
  GlobeAltIcon,
  BeakerIcon,
  CpuChipIcon,
  StarIcon,
  ArrowRightIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import FAQSection from '../components/FAQSection';
import useSEO from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';

const SERPfinityHome = () => {
  const { isAuthenticated } = useAuth();

  useSEO({
    title: 'SERPfinity - AI-Powered Search Visibility Platform | Master SEO & AEO',
    description: 'Dominate AI search results with SERPfinity. Track visibility across ChatGPT, Google AI, and 20+ SERP features. Get your free AI search visibility score today!',
    canonical: 'https://app.serpfinity.ai/',
    ogDescription: 'The only platform you need to dominate AI search results. Track, analyze, and optimize for ChatGPT, Google AI, and more.'
  });

  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard

  // Schema injection via useEffect (workaround for Helmet script tag limitation)
  useEffect(() => {
    const schemas = [
      organizationSchema,
      websiteSchema,
      softwareAppSchema,
      {"@context":"https://schema.org","@type":"FAQPage","mainEntity":HOME_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))}
    ];
    const scriptElements = [];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });
    return () => {
      scriptElements.forEach(el => el.remove());
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Schema markup for organization and website
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SERPfinity",
    "url": "https://app.serpfinity.ai",
    "logo": "https://app.serpfinity.ai/logo192.png",
    "description": "AI-Powered Search Visibility Platform for SEO and AEO optimization",
    "sameAs": [
      "https://twitter.com/serpfinity",
      "https://linkedin.com/company/serpfinity",
      "https://facebook.com/serpfinity"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-800-SERPFIN",
      "contactType": "customer service",
      "areaServed": "Worldwide",
      "availableLanguage": ["English", "Spanish", "French", "German"]
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SERPfinity",
    "url": "https://app.serpfinity.ai",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://app.serpfinity.ai/analyze?domain={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };


  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SERPfinity",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://app.serpfinity.ai",
    "description": "AI-powered SEO and Answer Engine Optimization platform that tracks visibility across ChatGPT, Perplexity, Google AI Overviews and traditional search engines.",
    "offers": [
      {
        "@type": "Offer",
        "name": "Starter Plan",
        "price": "17.00",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "17.00",
          "priceCurrency": "USD",
          "unitText": "MONTH"
        }
      },
      {
        "@type": "Offer",
        "name": "Business Plan",
        "price": "39.00",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "39.00",
          "priceCurrency": "USD",
          "unitText": "MONTH"
        }
      },
      {
        "@type": "Offer",
        "name": "Enterprise Plan",
        "price": "79.00",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "79.00",
          "priceCurrency": "USD",
          "unitText": "MONTH"
        }
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    }
  };

  const softwareAppSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SERPfinity",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://app.serpfinity.ai",
    "description": "AI-Powered Search Visibility Platform for SEO and AEO optimization. Track, analyze, and optimize your presence across ChatGPT, Google AI Overview, Perplexity AI, and more.",
    "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "17.00",
        "highPrice": "79.00",
        "priceCurrency": "USD",
        "offerCount": "3"
    },
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
    },
    "featureList": "AI Search Visibility Score, Answer Engine Optimization, AI Content Optimizer, Brand Sentiment Analysis, SERP Analysis, Rank Tracking, Keyword Research, Backlink Explorer, Competitor Analysis, Site Audit"
};

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Answer Engine Optimization (AEO)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Answer Engine Optimization (AEO) is the practice of optimizing your content to appear in AI-generated answers from tools like ChatGPT, Perplexity, Google AI Overviews, and other answer engines. Unlike traditional SEO, AEO focuses on structuring content so AI systems can extract and cite it as authoritative answers."
        }
      },
      {
        "@type": "Question",
        "name": "How does SERPfinity track AI search visibility?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SERPfinity monitors your brand and content mentions across major AI platforms including ChatGPT, Perplexity, Google AI Overviews, and Bing Copilot. It provides visibility scores, citation tracking, and actionable recommendations to improve how AI systems reference your content."
        }
      },
      {
        "@type": "Question",
        "name": "What SEO tools does SERPfinity include?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SERPfinity includes AI Search Visibility tracking, SERP Analysis, Content Optimization, AI Content Writer, Domain Analysis, Keyword Research, Competitive Analysis, Backlink Explorer, Site Audit, Rank Tracking, and Local SEO tools including Local Rankings and Listing Management."
        }
      },
      {
        "@type": "Question",
        "name": "How much does SERPfinity cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SERPfinity offers three pricing plans: Starter at $17/month for entrepreneurs and solopreneurs, Business at $39/month for small teams and growing businesses, and Enterprise at $79/month for agencies and large teams. All plans include a free trial."
        }
      },
      {
        "@type": "Question",
        "name": "Can SERPfinity help with local SEO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, SERPfinity includes dedicated Local SEO tools including Local Rankings tracking to monitor your positions in local search results, and Listing Management to keep your business information consistent across directories and platforms."
        }
      }
    ]
  };
  const handleAnalyze = (e) => {
    e.preventDefault();
    if (domain) {
      navigate(`/analyze?domain=${encodeURIComponent(domain)}`);
    }
  };

  const features = [
    {
      icon: <CpuChipIcon className="w-6 h-6" />,
      title: "AI Search Visibility Score",
      description: "Get instant insights into how AI search engines see and rank your content across ChatGPT, Perplexity, and Google AI Overview.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <SparklesIcon className="w-6 h-6" />,
      title: "Answer Engine Optimization",
      description: "Optimize for featured snippets, voice search, and AI-generated answers with our proprietary AEO scoring system.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <ChartBarIcon className="w-6 h-6" />,
      title: "SERP Feature Tracking",
      description: "Monitor and track your presence across 20+ SERP features including knowledge panels, People Also Ask, and local packs.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <DocumentTextIcon className="w-6 h-6" />,
      title: "AI Content Optimizer",
      description: "Generate and optimize content specifically for AI search algorithms using GPT-4 powered suggestions.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <GlobeAltIcon className="w-6 h-6" />,
      title: "Entity SEO Analysis",
      description: "Understand how search engines perceive your brand entity and improve your Knowledge Graph presence.",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: <BoltIcon className="w-6 h-6" />,
      title: "Real-Time SERP Analysis",
      description: "Get live SERP data and competitor insights updated every 24 hours for accurate tracking.",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  const aiSearchEngines = [
    { name: "ChatGPT", coverage: "95%", icon: "🤖" },
    { name: "Google AI Overview", coverage: "98%", icon: "🔍" },
    { name: "Perplexity AI", coverage: "92%", icon: "🧠" },
    { name: "Bing Chat", coverage: "90%", icon: "💬" },
    { name: "Claude", coverage: "88%", icon: "🎓" },
    { name: "You.com", coverage: "85%", icon: "🌐" }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "TechStartup Inc.",
      role: "CMO",
      content: "SERPfinity's AI visibility insights helped us increase our featured snippet appearances by 312% in just 3 months!",
      rating: 5,
      image: "https://ui-avatars.com/api/?name=Sarah+Johnson&background=6366f1&color=fff"
    },
    {
      name: "Michael Chen",
      company: "E-commerce Pro",
      role: "SEO Director",
      content: "The answer engine optimization features are game-changing. We're now appearing in ChatGPT responses 5x more often.",
      rating: 5,
      image: "https://ui-avatars.com/api/?name=Michael+Chen&background=10b981&color=fff"
    },
    {
      name: "Emma Davis",
      company: "Content Agency",
      role: "Founder",
      content: "Finally, a tool that understands the future of search. Our clients love the AI search visibility reports!",
      rating: 5,
      image: "https://ui-avatars.com/api/?name=Emma+Davis&background=f59e0b&color=fff"
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "$17",
      period: "/month",
      description: "Perfect for small businesses and bloggers",
      features: [
        "5 AI Visibility Scans/month",
        "Basic SERP tracking",
        "Answer Engine Score",
        "Schema markup analyzer",
        "Email support"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Business",
      price: "$39",
      period: "/month",
      description: "For growing businesses and agencies",
      features: [
        "Unlimited AI Visibility Scans",
        "Advanced SERP tracking",
        "AI Content Optimizer",
        "Competitor analysis",
        "Entity SEO dashboard",
        "API access",
        "Priority support"
      ],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "$79",
      period: "/month",
      description: "For large teams and enterprises",
      features: [
        "Everything in Business",
        "White-label reports",
        "Custom AI models",
        "Dedicated account manager",
        "Advanced API access",
        "Custom integrations",
        "SLA guarantee"
      ],
      cta: "Start Free Trial",
      popular: false
    }
  ];


  const HOME_FAQ = [
    { q: "What is SERPfinity and who is it for?", a: "SERPfinity is an all-in-one search visibility platform designed for SEO professionals, digital marketers, content strategists, and business owners who want to dominate both traditional and AI-powered search results. Our platform combines rank tracking, SERP analysis, keyword research, site auditing, content optimization, and AI search monitoring into a single unified dashboard." },
    { q: "How is SERPfinity different from other SEO tools?", a: "SERPfinity is uniquely built for the AI search era. While traditional SEO tools focus only on Google rankings, SERPfinity monitors your visibility across AI platforms including ChatGPT, Google AI Overviews, Perplexity, Claude, and more. We provide AEO optimization alongside traditional SEO tools, ensuring your content strategy covers the complete search landscape of today and tomorrow." },
    { q: "What is AEO and why should I care about it?", a: "AEO stands for Answer Engine Optimization, the practice of optimizing content to be selected as answers by AI-powered search engines and virtual assistants. As more users get answers from AI instead of clicking links, AEO ensures your brand remains visible and cited. SERPfinity provides the tools to monitor, measure, and improve your AI search presence alongside traditional SEO." },
    { q: "What tools does SERPfinity include?", a: "SERPfinity includes rank tracking for keywords across search engines, SERP analysis for feature opportunities, keyword research with AI insights, comprehensive site audits, content optimization scoring, backlink analysis, AI search visibility monitoring, local SEO tools including listing management and local rank tracking, content writing assistance, competitive intelligence, and detailed reporting." },
    { q: "Does SERPfinity offer a free trial?", a: "Yes, SERPfinity offers a free trial so you can experience the full power of our platform before committing. During the trial, you get access to all features including rank tracking, site auditing, keyword research, and AI search monitoring. No credit card is required to start your trial." },
    { q: "How does SERPfinity help with content marketing?", a: "SERPfinity accelerates content marketing by providing topic research to find high-opportunity subjects, keyword data to optimize targeting, content scoring to ensure SEO and AEO readiness, AI writing assistance for faster production, competitive content analysis, and performance tracking. Our platform helps you create content that ranks in search engines and gets cited by AI assistants." },
    { q: "What is AI Search Visibility?", a: "AI Search Visibility measures how often and prominently your content appears in AI-generated search results from platforms like ChatGPT, Google AI Overview, Perplexity, and Bing Chat. It's the next evolution of SEO, focusing on optimizing content for AI algorithms rather than just traditional search engines." },
    { q: "How quickly will I see results?", a: "Most users see improvements in their AI search visibility within 2-4 weeks of implementing our recommendations. Featured snippet appearances typically improve within 30 days, while Knowledge Graph updates may take 2-3 months depending on your industry and competition." },
    { q: "Do I need technical knowledge to use SERPfinity?", a: "Not at all! SERPfinity is designed to be user-friendly for everyone, from beginners to SEO experts. Our platform provides clear, actionable recommendations with step-by-step instructions. Plus, our support team is always ready to help you succeed." },
    { q: "Can I track my competitors?", a: "Yes! Business and Enterprise plans include comprehensive competitor tracking. You can monitor your competitors' AI search visibility, SERP features, and content strategies. This helps you identify gaps and opportunities to outrank them in AI-generated results." },
  ];




  return (
    <>
        <Helmet>
            <title>SERPfinity - AI-Powered Search Visibility Platform | Master SEO & AEO</title>
            <meta name="description" content="Dominate AI search results with SERPfinity. Track visibility across ChatGPT, Google AI, and 20+ SERP features. Get your free AI search visibility score today!" />
            <meta name="keywords" content="AI search visibility, answer engine optimization, AEO, SERP analysis, ChatGPT SEO, Google AI Overview, featured snippets, voice search optimization, entity SEO, knowledge graph" />
            <meta property="og:title" content="SERPfinity - Master AI Search Visibility & Answer Engine Optimization" />
            <meta property="og:description" content="The only platform you need to dominate AI search results. Track, analyze, and optimize for ChatGPT, Google AI, and more." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="SERPfinity - AI Search Visibility Platform" />
            <meta name="twitter:description" content="Track and optimize your visibility across AI search engines" />
            <link rel="canonical" href="https://app.serpfinity.ai" />
            <meta name="robots" content="index, follow" />
        </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
          
          <div className="relative container mx-auto px-4 py-24 sm:py-32">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <SparklesIcon className="w-5 h-5 text-yellow-300" />
                <span className="text-sm font-medium">AI Search Visibility is Here</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Master <span className="text-yellow-300">AI Search</span> Visibility
                <br />
                Dominate Every SERP
              </h1>
              
              <p className="text-xl sm:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
                The platform built for the AI search era. Track your visibility across ChatGPT, Google AI Overview, 
                and 20+ SERP features. Optimize content for both traditional and AI-powered search engines.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleAnalyze} className="max-w-2xl mx-auto mb-8">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                    id="hero-domain-input"
                    aria-label="Enter your domain for free AI search visibility analysis"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="Enter your domain (e.g., example.com)"
                      className="w-full px-6 py-4 text-lg text-gray-900 bg-white rounded-lg shadow-lg focus:outline-none focus:ring-4 focus:ring-yellow-300"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>Analyzing...</>
                    ) : (
                      <>
                        <MagnifyingGlassIcon className="w-5 h-5" />
                        Get Free AI Score
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-green-400" />
                  <span>100% Free Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <BoltIcon className="w-5 h-5 text-green-400" />
                  <span>Results in 60 seconds</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Animated Background Elements */}
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-10 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </section>

        {/* AI Search Engines Coverage */}
        <section className="py-12 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Track Your Visibility Across All AI Search Engines</h2>
              <p className="text-gray-600">Complete coverage of the AI search ecosystem</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {aiSearchEngines.map((engine, index) => (
                <motion.div
                  key={engine.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="text-4xl mb-2">{engine.icon}</div>
                  <div className="font-semibold text-gray-900">{engine.name}</div>
                  <div className="text-sm text-green-600 font-medium">{engine.coverage} Coverage</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Everything You Need to Dominate AI Search
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From traditional SEO to cutting-edge AI optimization, we've got you covered with the most comprehensive toolkit in the industry.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center text-white mb-4`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <Link to="/features" className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                    Learn more <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Get Your AI Visibility Score in 3 Simple Steps
              </h2>
              <p className="text-xl text-gray-600">
                Start optimizing for AI search engines in minutes
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-12">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Enter Your Domain</h3>
                    <p className="text-gray-600 text-lg">
                      Simply enter your website URL and our AI crawlers will analyze your entire site for search visibility opportunities.
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Instant AI Analysis</h3>
                    <p className="text-gray-600 text-lg">
                      Our platform analyzes how your content appears across ChatGPT, Google AI, Perplexity, and 20+ SERP features in real-time.
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Optimize & Dominate</h3>
                    <p className="text-gray-600 text-lg">
                      Follow our AI-powered recommendations to improve your visibility and start appearing in more AI-generated answers and featured snippets.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Trusted by Businesses Worldwide</h2>
              <p className="text-xl text-white/90">Join the AI search revolution</p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">95%</div>
                <div className="text-lg text-white/90">AI Visibility Improvement</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">3.2x</div>
                <div className="text-lg text-white/90">More Featured Snippets</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">250%</div>
                <div className="text-lg text-white/90">Increase in Voice Search</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">60s</div>
                <div className="text-lg text-white/90">To Complete Analysis</div>
              </div>
            </div>
          </div>
        </section>


        {/* Pricing Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-600">
                Choose the perfect plan for your business needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {pricingPlans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 1, y: 0 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true, amount: 0 }}
                  className={`relative bg-white rounded-2xl ${
                    plan.popular ? 'ring-2 ring-indigo-600 shadow-xl scale-105' : 'shadow-lg'
                  } p-8`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        MOST POPULAR
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`} onClick={() => navigate('/register')}>
                    {plan.cta}
                  </button>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600">
                All plans include a <span className="font-semibold">14-day free trial</span>. No credit card required.
              </p>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-4xl font-bold mb-6">
                Ready to Dominate AI Search Results?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Join businesses already optimizing for the future of search. 
                Get your free AI visibility score in less than 60 seconds.
              </p>
              
              <form onSubmit={handleAnalyze} className="max-w-md mx-auto mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    id="cta-domain-input"
                    aria-label="Enter your domain to get a free search visibility score"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="Enter your domain"
                    className="flex-1 px-6 py-3 text-gray-900 bg-white rounded-lg focus:outline-none focus:ring-4 focus:ring-yellow-300"
                    required
                  />
                  <button
                    type="submit"
                    className="px-8 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all transform hover:scale-105"
                  >
                    Get Free Score
                  </button>
                </div>
              </form>

              <p className="text-sm text-white/80">
                No credit card required • Free analysis • Results in 60 seconds
              </p>
            </motion.div>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
        <FAQSection items={HOME_FAQ} />
    </>
  );
};

export default SERPfinityHome;